/**
 * The betslip `source` token: `wg1:<product>:<placement>:<click_id>`.
 *
 * Grammar, vocabulary and test vectors live in docs/betslip-source-token.md.
 * The chatbot repo carries a twin of this file for sports search; both are
 * tested against those vectors, so change the doc first.
 */

import { createUuid } from '../utils/uuid'

export type SourceProduct = 'feed' | 'search' | 'share'

export const UNKNOWN_PLACEMENT = 'unknown'

const PRODUCTS: ReadonlySet<string> = new Set<SourceProduct>(['feed', 'search', 'share'])
const PLACEMENT_PATTERN = /^[a-z0-9_]{1,32}$/
const CLICK_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * Null for an unknown product or a malformed click id: a token that cannot be
 * joined back is worse than none. A placement outside the vocabulary's shape
 * is reported as `unknown` rather than dropping the product with it.
 */
export function formatSourceToken(product: SourceProduct, placement: string, clickId: string): string | null {
  if (!PRODUCTS.has(product)) return null
  const id = clickId.toLowerCase()
  if (!CLICK_ID_PATTERN.test(id)) return null
  const normalized = placement.toLowerCase()
  const slot = PLACEMENT_PATTERN.test(normalized) ? normalized : UNKNOWN_PLACEMENT
  return `wg1:${product}:${slot}:${id}`
}

export interface BetslipClick {
  /** Recorded with the click's intent so a placed bet's token joins back to it. */
  clickId: string
  /** The value for the host betslip's `source`. */
  source: string
}

/**
 * Mints the click id for one CTA click and the token that carries it. Every
 * entry the click adds shares it. Null only without Web Crypto, in which case
 * the add goes out with no source at all.
 */
export function createBetslipClick(product: SourceProduct, placement: string): BetslipClick | null {
  const clickId = createUuid()?.toLowerCase()
  if (!clickId) return null
  const source = formatSourceToken(product, placement, clickId)
  return source ? { clickId, source } : null
}
